import { normalize } from '@features/search';
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * Grille filtrable par recherche texte simple (light DOM).
 * L'hote passe les items et une fonction `matches(item, query)` + un renderer
 * `renderItem(item)`. Ce composant gere :
 * - input debounce (200 ms)
 * - affichage "Aucun resultat"
 * - layout CSS grid responsive
 *
 * Rendu en light DOM pour que les styles globaux s'appliquent au contenu
 * rendu par renderItem.
 */

type Matcher<T> = (item: T, query: string) => boolean;
type Renderer<T> = (item: T) => unknown;

@customElement('cof2-search-grid')
export class Cof2SearchGrid<T = unknown> extends LitElement {
  @property({ type: Array }) items: readonly T[] = [];
  @property({ attribute: false }) predicate: Matcher<T> = () => true;
  @property({ attribute: false }) renderItem: Renderer<T> = () => html``;
  @property({ type: String }) placeholder = 'Rechercher...';
  @state() private query = '';

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  protected createRenderRoot() {
    return this;
  }

  private onInput(e: Event): void {
    const value = (e.target as HTMLInputElement).value;
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.query = value;
    }, 200);
  }

  render() {
    const q = normalize(this.query.trim());
    const filtered = q ? this.items.filter((it) => this.predicate(it, q)) : this.items;
    return html`
      <div class="search-grid-toolbar">
        <input
          type="search"
          class="search-grid-input"
          placeholder=${this.placeholder}
          @input=${this.onInput}
          aria-label=${this.placeholder}
        />
      </div>
      ${
        filtered.length === 0
          ? html`<div class="search-grid-empty">Aucun resultat pour "${this.query}".</div>`
          : html`<div class="search-grid">${filtered.map((it) => this.renderItem(it))}</div>`
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cof2-search-grid': Cof2SearchGrid;
  }
}
